import { config } from '@/config'
import { getAdpApiHeaders } from '@/features/adp/api/adpDataSources.api'

function getAdpBaseUrl(): string {
  return config.adpApiUrl.replace(/\/$/, '')
}

/**
 * Base URL for legacy Akashic ingestion routes (file upload, show tables, show datatypes).
 * Matches paths used in Akashic-revamped-ui (`/file/uploadFile/v2`, `connection/showdatatypes/v2`, …).
 * Defaults to the ADP base URL; override with `VITE_INGESTION_API_URL` if those routes are on another host.
 */
function getIngestionBaseUrl(): string {
  return config.ingestionApiUrl.replace(/\/$/, '')
}

export type IngestionSourceObject = {
  databases: Array<Record<string, unknown>>
}

export type PresignedUrlDatabases = Array<Record<string, unknown> & { type: string; file_delimiter?: string }>

export type PresignedUrlRequestBody = {
  databases: PresignedUrlDatabases
  file_name: string
  content_type: string
}

export type PresignedUrlData = {
  upload_url: string
  path: string
  upload_id?: string
}

/**
 * `POST /files/presigned-url` — get S3 presigned URL + target path (ADP base URL).
 * Then PUT the file bytes to `upload_url`, then use `path` (e.g. s3://…) as `table_path` in showTables.
 */
export async function postFilesPresignedUrl(
  body: PresignedUrlRequestBody,
  baseUrl = getAdpBaseUrl()
): Promise<PresignedUrlData> {
  const url = `${baseUrl}/files/presigned-url`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as {
    success?: boolean
    message?: string
    data?: PresignedUrlData
  }
  if (!res.ok || !json.success || !json.data?.upload_url || !json.data?.path) {
    throw new Error(json.message || `Presigned URL request failed (${res.status})`)
  }
  return json.data
}

/**
 * Upload file to S3 using the presigned URL (client-side PUT; must match `content_type` used when generating the URL).
 */
export async function putFileToPresignedUrl(
  uploadUrl: string,
  file: File,
  contentType: string
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  })
  if (!res.ok) {
    throw new Error(`Upload to storage failed (${res.status})`)
  }
}

/** Derive MIME type for the presigned-URL request from the file name. */
export function contentTypeForSourceFileName(fileName: string): string {
  const ext = fileName.toLowerCase().split('.').pop() ?? ''
  switch (ext) {
    case 'csv':
      return 'text/csv'
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    case 'xls':
      return 'application/vnd.ms-excel'
    case 'txt':
      return 'text/plain'
    case 'tsv':
      return 'text/tab-separated-values'
    default:
      return 'application/octet-stream'
  }
}

export type IngestionUploadV2Response = {
  success?: boolean
  path?: string
  message?: string
}

/**
 * `POST /file/uploadFile/v2` — multipart `file` + `data` (JSON of `{ databases: [...] }`).
 * Revamped UI passes `databases: [{ type, file_delimiter? }]`.
 */
export async function uploadIngestionFileV2(
  file: File,
  data: IngestionSourceObject,
  baseUrl = getIngestionBaseUrl()
): Promise<IngestionUploadV2Response> {
  const form = new FormData()
  form.append('file', file)
  form.append('data', JSON.stringify(data))
  const url = `${baseUrl}/file/uploadFile/v2`
  const res = await fetch(url, {
    method: 'POST',
    headers: { ...getAdpApiHeaders() },
    body: form,
  })
  const raw = (await res.json().catch(() => ({}))) as IngestionUploadV2Response & { body?: IngestionUploadV2Response }
  const json = raw.body && typeof raw.body === 'object' ? raw.body : raw
  if (!res.ok) {
    throw new Error(json.message || `File upload failed (${res.status})`)
  }
  if (json && typeof json === 'object' && json.success === false) {
    throw new Error(json.message || 'File upload was not successful')
  }
  return json as IngestionUploadV2Response
}

export type IngestionShowTablesRow = {
  source_data_source_id?: number
  roo_id?: number
  tables?: string[] | { sheetPaths?: string[]; sheetNames?: string[]; tables?: string[] }
  databases?: Array<Record<string, unknown>>
  source_name?: string
  date_format?: string
  [k: string]: unknown
}

/**
 * `POST /source-data-sources` — registers source and returns sheet/table list.
 * This is used after presigned upload success (instead of legacy `/source_data_source/showTablesAndCreate/v2`).
 */
export async function postShowTablesAndCreateV2(
  sourceObj: IngestionSourceObject,
  baseUrl = getIngestionBaseUrl()
): Promise<IngestionShowTablesRow[]> {
  const url = `${baseUrl}/source-data-sources`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sourceObj),
  })
  const json = (await res.json().catch(() => ({}))) as
    | IngestionShowTablesRow[]
    | IngestionShowTablesRow
    | { data?: IngestionShowTablesRow[] | IngestionShowTablesRow; success?: boolean; message?: string }
  if (!res.ok) {
    const msg = typeof json === 'object' && json && 'message' in json ? String((json as { message?: string }).message) : ''
    throw new Error(msg || `source-data-sources create failed (${res.status})`)
  }
  if (Array.isArray(json)) {
    return json
  }
  if (json && typeof json === 'object' && 'data' in json) {
    const payload = (json as { data?: IngestionShowTablesRow[] | IngestionShowTablesRow }).data
    if (Array.isArray(payload)) return payload
    if (payload && typeof payload === 'object') return [payload]
  }
  if (json && typeof json === 'object' && !('success' in json) && !('message' in json)) {
    return [json as IngestionShowTablesRow]
  }
  return []
}

/**
 * `POST /connection/showdatatypes/v2` — column types for a selected sheet (Excel / file flow).
 */
export async function postIngestionShowDataTypesV2(
  sourceObj: IngestionSourceObject,
  baseUrl = getIngestionBaseUrl()
): Promise<Array<Record<string, unknown>>> {
  const url = `${baseUrl}/connection/showdatatypes/v2`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sourceObj),
  })
  const json = (await res.json().catch(() => ({}))) as
    | Array<Record<string, unknown>>
    | { data?: Array<Record<string, unknown>>; success?: boolean; message?: string }
  if (!res.ok) {
    const msg = typeof json === 'object' && json && 'message' in json ? String((json as { message?: string }).message) : ''
    throw new Error(msg || `showDataTypes failed (${res.status})`)
  }
  if (Array.isArray(json)) {
    return json
  }
  if (Array.isArray((json as { data?: Array<Record<string, unknown>> }).data)) {
    return (json as { data: Array<Record<string, unknown>> }).data
  }
  return []
}

/** One row as returned from `getSheetDataTypesV2` (detect_fields). */
export type IngestionSheetDataTypeRow = {
  columns?: string[]
  datatypes?: string[]
  field_structures?: string[]
}

/**
 * `POST /detect_fields/getdatatypes/v2` — per-sheet details for xlsx in filesystem / multi-table flows.
 * Revamped `ApiService.getSheetDataTypesV2` uses this path.
 */
export async function postIngestionGetSheetDataTypesV2(
  sourceObj: IngestionSourceObject,
  baseUrl = getIngestionBaseUrl()
): Promise<IngestionSheetDataTypeRow[]> {
  const url = `${baseUrl}/detect_fields/getdatatypes/v2`
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      ...getAdpApiHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sourceObj),
  })
  const json = (await res.json().catch(() => ({}))) as
    | IngestionSheetDataTypeRow[]
    | { data?: IngestionSheetDataTypeRow[]; success?: boolean; message?: string }
  if (!res.ok) {
    const msg = typeof json === 'object' && json && 'message' in json ? String((json as { message?: string }).message) : ''
    throw new Error(msg || `getSheetDataTypes failed (${res.status})`)
  }
  if (Array.isArray(json)) {
    return json
  }
  if (Array.isArray((json as { data?: IngestionSheetDataTypeRow[] }).data)) {
    return (json as { data: IngestionSheetDataTypeRow[] }).data
  }
  return []
}

/**
 * Parsers `v.tables` for Excel: builds sheetPaths + sheetNames (same logic as register-source in revamped UI).
 */
export function parseExcelSheetsFromTablePaths(
  tablePaths: string[] | null | undefined
): { sheetNames: string[]; sheetPaths: string[] } {
  const outNames: string[] = []
  const outPaths: string[] = []
  if (!Array.isArray(tablePaths)) {
    return { sheetNames: outNames, sheetPaths: outPaths }
  }
  for (const e of tablePaths) {
    if (typeof e !== 'string') continue
    const parts = e.split('/')
    if (parts.length === 0) continue
    const last = parts[parts.length - 1] ?? ''
    const sheet = last.split('.')
    if (sheet.length > 0) {
      outPaths.push(e)
      outNames.push(sheet[0] ?? e)
    }
  }
  return { sheetNames: outNames, sheetPaths: outPaths }
}
